import React from 'react';
import { Link } from 'react-router-dom';
import { MailOutlined, Facebook, Instagram, WhatsApp, VerifiedUser } from '@mui/icons-material';

const Footer = () => {
  return (
    <footer className="relative w-full overflow-hidden text-slate-200 pt-16 sm:pt-24 pb-8 sm:pb-12 mt-auto border-t border-slate-900 bg-[#070a13]">
      
      {/* Main Footer Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Main Footer Content Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 sm:gap-12 mb-10 sm:mb-16 items-start">
          
          {/* Logo, Description, Registration & Social Media Icons */}
          <div className="col-span-2 md:col-span-1 space-y-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-headline font-light text-white tracking-widest uppercase">
                JAI <span className="text-amber-400 font-normal">LANKA</span>
              </h2>
              <p className="text-[9px] sm:text-[10px] text-amber-500/60 uppercase tracking-[0.2em] mt-1">Tours</p>
            </div>
            
            <p className="text-slate-400 text-sm font-light leading-relaxed max-w-sm text-left md:text-justify">
              Hallmark of Sri Lankan Travel. Specializing in bespoke luxury tours and curated exotic experiences for the modern traveler.
            </p>

            {/* 🛡️ Government Registration Badge */}
            <div className="p-3 sm:p-3.5 rounded-xl bg-slate-900/80 border border-amber-500/20 backdrop-blur-sm max-w-sm w-full md:w-auto">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-2.5 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0">
                  <VerifiedUser style={{ fontSize: '20px' }} />
                </div>
                <div>
                  <p className="text-[9px] sm:text-[10px] font-bold text-amber-400 tracking-wider uppercase">
                    JAI LANKA TOURS (PRIVATE) LIMITED
                  </p>
                  <p className="text-[10px] sm:text-[11px] font-medium text-slate-200 tracking-wide mt-0.5">
                    Government Registered Agency
                  </p>
                  <p className="text-[9px] sm:text-[10px] text-slate-400 font-mono mt-0.5">
                    Reg No: <span className="text-slate-300 font-semibold">PV 00373092</span>
                  </p>
                </div>
              </div>
            </div>

            {/* 🌐 Modern Social Media Icons Wrapper */}
            <div className="flex items-center gap-3 pt-2">
              <a 
                href="https://www.facebook.com/share/1DF32gN2H4/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-slate-900/40 border border-slate-800/80 text-slate-400 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] hover:shadow-lg hover:shadow-[#1877F2]/20 flex items-center justify-center transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook style={{ fontSize: '18px' }} />
              </a>

              <a 
                href="https://www.instagram.com/jailankatours?igsh=bzJycHRzb2RnYnBw&utm_source=ig_contact_invite" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-slate-900/40 border border-slate-800/80 text-slate-400 hover:text-white hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7] hover:border-transparent hover:shadow-lg hover:shadow-[#ee2a7b]/20 flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram style={{ fontSize: '18px' }} />
              </a>

              <a 
                href="https://wa.me/94740966449" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-slate-900/40 border border-slate-800/80 text-slate-400 hover:text-white hover:bg-[#25D366] hover:border-[#25D366] hover:shadow-lg hover:shadow-[#25D366]/20 flex items-center justify-center transition-all duration-300"
                aria-label="WhatsApp"
              >
                <WhatsApp style={{ fontSize: '18px' }} />
              </a>

              <a 
                href="mailto:jailankatours@gmail.com" 
                className="w-10 h-10 rounded-xl bg-slate-900/40 border border-slate-800/80 text-slate-400 hover:text-white hover:bg-[#EA4335] hover:border-[#EA4335] hover:shadow-lg hover:shadow-[#EA4335]/20 flex items-center justify-center transition-all duration-300"
                aria-label="Email"
              >
                <MailOutlined style={{ fontSize: '18px' }} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1">
            <h5 className="font-bold text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] uppercase mb-5 sm:mb-8 text-slate-500">Quick Links</h5>
            <ul className="space-y-3 sm:space-y-4 text-[13px] sm:text-sm font-light text-slate-400">
              <li><Link to="/" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">Home</Link></li>
              <li><Link to="/day-tours" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">Day Tours</Link></li>
              <li><Link to="/itineraries" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">Itineraries</Link></li>
              <li><Link to="/discoversrilanka" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Discover Sri Lanka</Link></li>
              <li><Link to="/accommodation" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">Accommodation</Link></li>
              <li><Link to="/about-us" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">About Us</Link></li>
              <li><Link to="/plan-journey" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block">Plan Journey</Link></li>
            </ul>
          </div>

          {/* Experiences */}
          <div className="col-span-1">
            <h5 className="font-bold text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] uppercase mb-5 sm:mb-8 text-slate-500">Experiences</h5>
            <ul className="space-y-3 sm:space-y-4 text-[13px] sm:text-sm font-light text-slate-400">
              <li><Link to="/itineraries/ayurvedic" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Ayurvedic & Wellness</Link></li>
              <li><Link to="/itineraries/adventure" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Off Road Adventure</Link></li>
              <li><Link to="/itineraries/culture" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Culture & Wildlife</Link></li>
              <li><Link to="/itineraries/differently-abled" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Differently Abled</Link></li>
              <li><Link to="/itineraries/romantic" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">Romantic Tours</Link></li>
              <li><Link to="/itineraries/north-east" className="hover:text-amber-400 hover:pl-1 transition-all duration-300 block leading-tight">North & East Coast</Link></li>
            </ul>
          </div>

          {/* Contact Details */}
          <div className="col-span-2 md:col-span-1 md:pl-6">
            <h5 className="font-bold text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] uppercase mb-5 sm:mb-8 text-slate-500">Contact Us</h5>
            <ul className="space-y-4 sm:space-y-5 text-[13px] sm:text-sm font-light text-slate-400">
              <li className="flex flex-col gap-0.5">
                <span className="text-[9px] sm:text-[10px] text-slate-600 font-bold uppercase tracking-wider">Hotline</span>
                <a href="tel:+94740966449" className="text-slate-300 hover:text-amber-400 transition-colors">+9474 096 6449</a>
              </li>
              <li className="flex flex-col gap-0.5">
                <span><Link to="/enquiry" className="text-[9px] sm:text-[10px] text-slate-600 font-bold uppercase tracking-wider hover:text-amber-400">Inquiries</Link></span>
                <a href="mailto:jailankatours@gmail.com" className="text-slate-300 hover:text-amber-400 transition-colors">jailankatours@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Copyright Bar */}
        <div className="pt-6 sm:pt-8 border-t border-slate-800/60 flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6 text-center md:text-left">
          <p className="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium leading-relaxed">
            © 2026 Jai Lanka Tours. All Rights Reserved. <span className="hidden md:inline mx-1">|</span> <span className="block md:inline text-slate-600 mt-1 md:mt-0">JAI LANKA TOURS (PVT) LTD - Reg No: PV 00373092</span>
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium">
            <Link to="/privacy-policy" className="hover:text-amber-400 cursor-pointer transition-all duration-300">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-amber-400 cursor-pointer transition-all duration-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;